import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, CreditCard, Receipt, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trialing: "bg-sky-50 text-sky-700 border-sky-200",
  past_due: "bg-amber-50 text-amber-700 border-amber-200",
  canceled: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

export default function Account() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [myPlan, setMyPlan] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [deletionState, setDeletionState] = useState("idle");
  const [deletionRef, setDeletionRef] = useState(null);

  const requestDeletion = async () => {
    if (!window.confirm("Request deletion of your DISJINX account and all associated data? We'll email disjinx.support@gmail.com on your behalf.")) return;
    setDeletionState("sending");
    try {
      const res = await fetch(`${API}/account/request-deletion`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Request failed");
      setDeletionRef(data.ref);
      setDeletionState("sent");
      toast.success(
        data.already_requested
          ? `You've already requested deletion — reference ${data.ref}.`
          : `Deletion request sent — reference ${data.ref}.`
      );
    } catch (err) {
      setDeletionState("idle");
      toast.error(err.message);
    }
  };

  useEffect(() => {
    if (location.state?.user) return;
    const check = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    };
    check();
  }, [location.state]);

  useEffect(() => {
    if (authed !== true) return;
    (async () => {
      try {
        const res = await fetch(`${API}/billing/overview`, { credentials: "include" });
        if (!res.ok) throw new Error();
        setBilling(await res.json());
      } catch {
        setBilling({ has_account: false, subscriptions: [], invoices: [] });
      } finally {
        setBillingLoading(false);
      }
      try {
        const res = await fetch(`${API}/billing/my-plan`, { credentials: "include" });
        if (res.ok) setMyPlan(await res.json());
        else setMyPlan({ has_plan: false });
      } catch {
        setMyPlan({ has_plan: false });
      }
    })();
  }, [authed]);

  const cancelPlan = async (subId, mode) => {
    setCancelling(`${subId}-${mode}`);
    try {
      const res = await fetch(`${API}/billing/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sub_id: subId, mode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not cancel");
      toast.success(mode === "immediate" ? "Plan cancelled — automation switched off." : "Plan will cancel at the end of the current weekly period — no fees.");
      const refresh = await fetch(`${API}/billing/my-plan`, { credentials: "include" });
      if (refresh.ok) setMyPlan(await refresh.json());
      setConfirming(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(`${API}/billing/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not open billing portal");
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err.message);
      setPortalLoading(false);
    }
  };

  if (authed === null) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-32">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </main>
    );
  }

  if (authed === false) {
    return (
      <main data-testid="account-signed-out" className="flex min-h-[70vh] items-center pt-32 pb-24">
      <Seo title="My Billing — DISJINX" description="Manage your DISJINX automation plan, billing and invoices." path="/account" noindex />
        <div className="mx-auto max-w-lg px-6 text-center">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Sign in to view your billing</h1>
          <p className="mt-6 text-base text-neutral-600">
            Use the same Google account email you subscribed with — we'll match it to your plan automatically.
          </p>
          <Link
            to="/signin"
            data-testid="account-signin-button"
            className="mt-10 inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
          >
            Sign in with Google
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="account-page" className="pt-40 pb-32 lg:pt-48">
      <Seo title="My Billing — DISJINX" description="Manage your DISJINX automation plan, billing and invoices." path="/account" noindex />
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">My billing</p>
              <h1 data-testid="account-greeting" className="font-display text-4xl font-extrabold tracking-tight lg:text-5xl">
                Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
              </h1>
              <p data-testid="account-email" className="mt-3 text-sm text-neutral-500">{user?.email}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {user?.is_admin && (
                <Link
                  to="/admin"
                  data-testid="account-admin-link"
                  className="inline-flex items-center gap-2 bg-neutral-950 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
                >
                  Admin panel
                </Link>
              )}
              <button
                data-testid="account-logout-button"
                onClick={async () => { await logout(); navigate("/"); }}
                className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-3 text-sm font-semibold transition-colors duration-200 hover:border-neutral-950"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>

          {myPlan === null ? (
            <div className="mt-16 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
            </div>
          ) : myPlan.has_plan ? (
            <>
              <div data-testid="usage-totals" className="mt-16 grid gap-px border border-neutral-200 bg-neutral-200 sm:grid-cols-2">
                <div className="bg-white p-8 lg:p-10">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">This Week's Total Credit Usage</p>
                  <p data-testid="total-credit-usage" className="mt-3 font-display text-3xl font-extrabold tracking-tight">
                    {myPlan.total_weekly_credits} credits used
                  </p>
                </div>
                <div className="bg-white p-8 lg:p-10">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">This Week's Total Usage Fee</p>
                  <p data-testid="total-usage-fee" className="mt-3 font-display text-3xl font-extrabold tracking-tight">
                    ${myPlan.total_weekly_usage_fee.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="mt-px space-y-px">
                {myPlan.plans.map((plan, idx) => (
                  <div key={plan.sub_id} data-testid={`my-plan-card-${idx}`} className="grid gap-8 border border-t-0 border-neutral-200 bg-white p-8 lg:grid-cols-12 lg:p-10">
                    <div className="lg:col-span-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="font-display text-2xl font-extrabold tracking-tight">{plan.plan_label} Plan</h2>
                        <span className={`border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${plan.cancel_at_period_end ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                          {plan.cancel_at_period_end ? "cancels at period end" : "active"}
                        </span>
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                        ${plan.weekly_price.toFixed(2)} AUD/week
                        {" · "}
                        {plan.cancel_at_period_end ? "ends" : "renews"}{" "}
                        {new Date(plan.current_period_end).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
                        {" · usage rate $"}{plan.credit_rate}/credit
                      </p>
                    </div>
                    <div className="grid gap-px border border-neutral-200 bg-neutral-200 sm:grid-cols-2 lg:col-span-4">
                      <div className="bg-neutral-50 p-5">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500">This Week's Credit Usage</p>
                        <p data-testid={`plan-credits-${idx}`} className="mt-2 text-sm font-bold text-neutral-950">{plan.weekly_credits} credits used</p>
                      </div>
                      <div className="bg-neutral-50 p-5">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500">This Week's Usage Fee</p>
                        <p data-testid={`plan-usage-fee-${idx}`} className="mt-2 text-sm font-bold text-neutral-950">${plan.weekly_usage_fee.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="flex items-start lg:col-span-3 lg:justify-end">
                      {!plan.cancel_at_period_end && (
                        confirming === plan.sub_id ? (
                          <div className="w-full border border-amber-200 bg-amber-50 p-4">
                            <p className="text-sm font-medium text-amber-900">Cancel this plan? No cancellation fees.</p>
                            <div className="mt-3 flex flex-col gap-2">
                              <button
                                data-testid={`cancel-period-end-${idx}`}
                                onClick={() => cancelPlan(plan.sub_id, "period_end")}
                                disabled={cancelling !== null}
                                className="bg-neutral-950 px-4 py-2.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-neutral-800 disabled:opacity-50"
                              >
                                {cancelling === `${plan.sub_id}-period_end` ? "Cancelling…" : "At end of weekly period"}
                              </button>
                              <button
                                data-testid={`cancel-immediate-${idx}`}
                                onClick={() => cancelPlan(plan.sub_id, "immediate")}
                                disabled={cancelling !== null}
                                className="border border-red-300 px-4 py-2.5 text-xs font-semibold text-red-600 transition-colors duration-200 hover:border-red-500 disabled:opacity-50"
                              >
                                {cancelling === `${plan.sub_id}-immediate` ? "Cancelling…" : "Immediately (switch off now)"}
                              </button>
                              <button
                                data-testid={`cancel-keep-${idx}`}
                                onClick={() => setConfirming(null)}
                                className="px-4 py-2 text-xs font-semibold text-neutral-600 transition-colors duration-200 hover:text-neutral-950"
                              >
                                Keep plan
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            data-testid={`cancel-button-${idx}`}
                            onClick={() => setConfirming(plan.sub_id)}
                            className="border border-neutral-300 px-5 py-3 text-sm font-semibold transition-colors duration-200 hover:border-neutral-950"
                          >
                            Cancel plan
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div data-testid="my-plan-empty" className="mt-16 border border-neutral-200 bg-neutral-50 p-10 lg:p-14">
              <h2 className="font-display text-2xl font-bold tracking-tight">No plan yet</h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600">
                Add a plan to your cart and check out — once your automation is built and running, your plans
                will appear here, where you can track usage and manage or cancel any time, with no cancellation fees.
              </p>
              <Link
                to="/"
                data-testid="my-plan-view-plans-link"
                className="mt-8 inline-flex items-center gap-2 bg-neutral-950 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
              >
                View plans
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {billingLoading ? (
            <div className="mt-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
            </div>
          ) : !billing?.has_account ? null : (
            <>
              <div className="mt-16 grid gap-px border border-neutral-200 bg-neutral-200 lg:grid-cols-2">
                <div className="bg-white p-10 lg:p-12">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5" strokeWidth={1.5} />
                    <h2 className="font-display text-xl font-bold tracking-tight">Your plan</h2>
                  </div>
                  {billing.subscriptions.length === 0 ? (
                    <p className="mt-6 text-sm text-neutral-600">No active subscriptions on this account.</p>
                  ) : (
                    <div className="mt-8 space-y-6">
                      {billing.subscriptions.map((s) => (
                        <div key={s.id} data-testid={`subscription-${s.id}`} className="border border-neutral-200 p-6">
                          <div className="flex items-center justify-between gap-4">
                            <p className="font-display text-lg font-bold">{s.plan}</p>
                            <span className={`border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${STATUS_STYLES[s.status] || STATUS_STYLES.canceled}`}>
                              {s.cancel_at_period_end && s.status === "active" ? "cancels at period end" : s.status}
                            </span>
                          </div>
                          <p className="mt-3 text-sm text-neutral-600">
                            ${s.amount?.toFixed(2)} {s.currency}/{s.interval}
                            {s.status === "trialing" ? (
                              <> · weekly fee starts when your automation is live</>
                            ) : (
                              s.current_period_end && (
                                <> · {s.cancel_at_period_end ? "ends" : "renews"} {new Date(s.current_period_end).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</>
                              )
                            )}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    data-testid="manage-billing-button"
                    onClick={openPortal}
                    disabled={portalLoading}
                    className="group mt-10 inline-flex items-center gap-2 bg-neutral-950 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800 disabled:opacity-50"
                  >
                    {portalLoading ? (
                      <>Opening portal <Loader2 className="h-4 w-4 animate-spin" /></>
                    ) : (
                      <>Manage billing <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                    )}
                  </button>
                  <p className="mt-4 text-xs text-neutral-500">Update your card, change or cancel your plan, download receipts — securely via Stripe.</p>
                </div>

                <div className="bg-white p-10 lg:p-12">
                  <div className="flex items-center gap-3">
                    <Receipt className="h-5 w-5" strokeWidth={1.5} />
                    <h2 className="font-display text-xl font-bold tracking-tight">Recent invoices</h2>
                  </div>
                  {billing.invoices.length === 0 ? (
                    <p className="mt-6 text-sm text-neutral-600">No invoices yet.</p>
                  ) : (
                    <div className="mt-8 divide-y divide-neutral-200 border-t border-b border-neutral-200">
                      {billing.invoices.map((inv) => (
                        <a
                          key={inv.id}
                          data-testid={`invoice-${inv.id}`}
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center justify-between py-4 text-sm"
                        >
                          <div>
                            <p className="font-semibold text-neutral-950">{inv.number || inv.id.slice(-8)}</p>
                            <p className="text-xs text-neutral-500">{new Date(inv.created).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold">${inv.amount_paid.toFixed(2)} {inv.currency}</span>
                            <ArrowUpRight className="h-4 w-4 text-neutral-400 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div data-testid="privacy-section" className="mt-16 border border-neutral-200 bg-neutral-50 p-10 lg:p-12">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5" strokeWidth={1.5} />
              <h2 className="font-display text-xl font-bold tracking-tight">Privacy &amp; data</h2>
            </div>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-neutral-600">
              You can request deletion of your account and everything we hold about you — profile, enquiries,
              chat history and billing records. This emails our support team, and we'll confirm once it's done.
            </p>
            <button
              data-testid="request-deletion-button"
              onClick={requestDeletion}
              disabled={deletionState !== "idle"}
              className="mt-8 inline-flex items-center gap-2 border border-red-300 px-6 py-3.5 text-sm font-semibold text-red-700 transition-colors duration-200 hover:border-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {deletionState === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
              {deletionState === "sent" ? "Deletion requested" : "Request account & data deletion"}
            </button>
            {deletionRef && (
              <p data-testid="deletion-ref" className="mt-4 text-xs text-neutral-500">
                Request received — reference {deletionRef}. We'll action it within 30 days and confirm by email.
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </main>
  );
}
